import React, { useEffect, lazy, Suspense } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation, useSearchParams } from 'react-router-dom';
import { AppProvider, useAppContext } from './contexts/AppContext';
import { ToastProvider } from './contexts/ToastContext';
import { visaAPI } from './services/api';
import LoadingScreen from './components/LoadingScreen';
import DelegationBanner from './components/DelegationBanner';
import OnboardingTour from './components/OnboardingTour';
import ChatWidget from './components/ChatWidget';

// Route-bazlı code-split: sayfalar lazy yüklenir (tek 1.5MB chunk yerine sayfa başına parça).
// Özellikle AdminPage (~5K satır) normal kullanıcının ilk indirmesine girmez.
const LoginPage = lazy(() => import('./pages/LoginPage'));
const MainPage = lazy(() => import('./pages/MainPage'));
const WelcomePage = lazy(() => import('./pages/WelcomePage'));
const StartPage = lazy(() => import('./pages/StartPage'));
const PaymentPage = lazy(() => import('./pages/PaymentPage'));
const ChatPage = lazy(() => import('./pages/ChatPage'));
const ProfilePage = lazy(() => import('./pages/ProfilePage'));
const AccountPage = lazy(() => import('./pages/AccountPage'));
const RetentionDecisionPage = lazy(() => import('./pages/RetentionDecisionPage'));
const DeletionCancelPage = lazy(() => import('./pages/DeletionCancelPage'));
const InvitationPage = lazy(() => import('./pages/InvitationPage'));
const GroupPage = lazy(() => import('./pages/GroupPage'));
const DocumentsPage = lazy(() => import('./pages/DocumentsPage'));
const AdminPage = lazy(() => import('./pages/AdminPage'));
const ApplicationsPage = lazy(() => import('./pages/ApplicationsPage'));
const VerifyEmailPage = lazy(() => import('./pages/VerifyEmailPage'));
const ForgotPasswordPage = lazy(() => import('./pages/ForgotPasswordPage'));
const ResetPasswordPage = lazy(() => import('./pages/ResetPasswordPage'));
const ChangePasswordPage = lazy(() => import('./pages/ChangePasswordPage'));
const KnowledgeBasePage = lazy(() => import('./pages/KnowledgeBasePage'));
const AppointmentsTrackPage = lazy(() => import('./pages/AppointmentsTrackPage'));
const CoverLetterPage = lazy(() => import('./pages/CoverLetterPage'));
const PaymentSuccessPage = lazy(() => import('./pages/PaymentSuccessPage'));
const PaymentErrorPage = lazy(() => import('./pages/PaymentErrorPage'));
const ContractViewPage = lazy(() => import('./pages/ContractViewPage'));
const AkbankChannelPage = lazy(() => import('./pages/AkbankChannelPage'));

// Sohbet balonu: /chat'te zaten sohbet var; /start'ta alttaki yapışkan karar çubuğunun
// (Ödemeye geç) üstüne biniyor.
const CHAT_WIDGET_HIDDEN_PATHS = ['/chat', '/start'];

// Where to send a logged-in user when landing on "/", "/login" or "/register".
// - test/admin: always see the welcome (country recommendation) screen.
// - normal users: welcome only on their first visit (until they complete it).
function getLandingPath(user, welcomeSeen) {
  const role = user?.Role;
  if (role === 'admin' || role === 'test') return '/welcome';
  if (welcomeSeen === false) return '/welcome';
  return '/main';
}

// Sadece uygulama-içi göreli yollara izin ver (açık yönlendirme/open-redirect önlemi).
function safeRedirect(raw) {
  if (!raw) return null;
  const path = decodeURIComponent(raw);
  // "/..." kabul; "//host" veya "/\..." protokol-göreli saldırılarını reddet.
  if (!path.startsWith('/') || path.startsWith('//') || path.startsWith('/\\')) return null;
  return path;
}

// Protected Route Component
function ProtectedRoute({ children }) {
  const { state } = useAppContext();
  const location = useLocation();

  if (!state.currentUser) {
    // Login sonrası geri dönebilmek için hedefi ?redirect ile taşı (ör. e-postadaki /contracts/:id).
    const target = encodeURIComponent(location.pathname + location.search);
    return <Navigate to={`/login?redirect=${target}`} replace />;
  }

  return children;
}

// /login ve /register: girişli kullanıcıyı ?redirect hedefine (varsa) ya da landing'e yolla.
function LoginRoute() {
  const { state } = useAppContext();
  const [searchParams] = useSearchParams();

  if (state.currentUser) {
    const redirect = safeRedirect(searchParams.get('redirect'));
    return <Navigate to={redirect || getLandingPath(state.currentUser, state.welcomeSeen)} replace />;
  }

  return <LoginPage />;
}

// Payment Protected Route Component
function PaymentProtectedRoute({ children }) {
  const { state } = useAppContext();

  if (!state.currentUser) {
    return <Navigate to="/login" replace />;
  }

  // if (!state.hasCompletedPayment) {
  //   return <Navigate to="/main" replace />;
  // }

  return children;
}

// Admin Protected Route Component
function AdminProtectedRoute({ children }) {
  const { state } = useAppContext();

  // Wait for session verification to complete before making routing decisions
  if (state.isLoading) {
    return <LoadingScreen />;
  }

  if (!state.currentUser) {
    return <Navigate to="/login" replace />;
  }
  if (state.currentUser.Role !== 'admin') {
    return <Navigate to="/main" replace />;
  }
  return children;
}

function AppRoutes() {
  const { state, dispatch } = useAppContext();
  const location = useLocation();

  // Load visa tips on app start
  useEffect(() => {
    const loadVisaTips = async () => {
      try {
        const tips = await visaAPI.loadVisaTips();
        dispatch({ type: 'SET_VISA_TIPS', payload: tips });
      } catch (error) {
        console.error('Failed to load visa tips:', error);
      }
    };

    loadVisaTips();
  }, [dispatch]);

  if (state.isLoading) {
    return <LoadingScreen />;
  }

  return (
    <>
    {/* Vekalet bandı rota DIŞINDA: hangi sayfada olunursa olunsun "kimin adına" görünür. */}
    <DelegationBanner />
    <Suspense fallback={<LoadingScreen />}>
    <Routes>
      <Route path="/login" element={<LoginRoute />} />

      <Route path="/register" element={<LoginRoute />} />

      <Route path="/welcome" element={
        <ProtectedRoute>
          <WelcomePage />
        </ProtectedRoute>
      } />

      {/* Ara ekran: ülke + başlangıç (ücretsiz / paket) tek yerde seçilir. */}
      <Route path="/start" element={
        <ProtectedRoute>
          <StartPage />
        </ProtectedRoute>
      } />

      <Route path="/main" element={
        <ProtectedRoute>
          <MainPage />
        </ProtectedRoute>
      } />

      <Route path="/payment" element={
        <ProtectedRoute>
          <PaymentPage />
        </ProtectedRoute>
      } />

      {/* Satın alma e-postasındaki "belgeyi görüntüle" linkleri (sözleşme nüshaları, sahibine özel) */}
      <Route path="/contracts/:id" element={
        <ProtectedRoute>
          <ContractViewPage />
        </ProtectedRoute>
      } />

      <Route path="/chat" element={
        <PaymentProtectedRoute>
          <ChatPage />
        </PaymentProtectedRoute>
      } />

      <Route path="/applications" element={
        <PaymentProtectedRoute>
          <ApplicationsPage />
        </PaymentProtectedRoute>
      } />

      <Route path="/appointments-track" element={
        <PaymentProtectedRoute>
          <AppointmentsTrackPage />
        </PaymentProtectedRoute>
      } />

      <Route path="/cover-letter" element={
        <PaymentProtectedRoute>
          <CoverLetterPage />
        </PaymentProtectedRoute>
      } />

      <Route path="/profile" element={
        <PaymentProtectedRoute>
          <ProfilePage />
        </PaymentProtectedRoute>
      } />

      {/* Grup yönetimi: ödeme kapısı YOK. Erişim üyelikten doğuyor (admin'in verdiği grup
          yetkisi = ürünü satın almış olmak), o yüzden burada ödeme kontrolü aranmaz. */}
      <Route path="/grubum" element={
        <ProtectedRoute>
          <GroupPage />
        </ProtectedRoute>
      } />

      {/* Hesabım: bilerek ProtectedRoute — KVKK md.11 hakları (verilere erişim, silme,
          rıza geri alma) ödeme durumuna bağlanamaz. */}
      <Route path="/hesabim" element={
        <ProtectedRoute>
          <AccountPage />
        </ProtectedRoute>
      } />

      <Route path="/documents" element={
        <PaymentProtectedRoute>
          <DocumentsPage />
        </PaymentProtectedRoute>
      } />

      <Route path="/admin" element={
        <AdminProtectedRoute>
          <AdminPage />
        </AdminProtectedRoute>
      } />

      <Route path="/knowledge-base" element={
        <AdminProtectedRoute>
          <KnowledgeBasePage />
        </AdminProtectedRoute>
      } />

      {/* Authentication Routes */}
      <Route path="/verify-email" element={<VerifyEmailPage />} />
      <Route path="/forgot-password" element={<ForgotPasswordPage />} />
      <Route path="/reset-password" element={<ResetPasswordPage />} />

      {/* KVKK saklama kararı: bilinçli olarak public. E-posta gelen kullanıcı çoktan
          çıkmış olabilir; "verilerimi silin" demenin önüne giriş duvarı koymak KVKK
          md.11 hakkını engellemek olur. Yetki, URL'deki hash'li token ile sağlanıyor. */}
      <Route path="/kvkk/karar" element={<RetentionDecisionPage />} />

      {/* Hesap silme geri alma bağlantısı — public. Bu bağlantının varlık sebebi hesabına
          giremeyen kullanıcıyı kurtarmak; giriş duvarı koymak onu işlevsiz bırakırdı. */}
      <Route path="/hesap-silme/iptal" element={<DeletionCancelPage />} />

      {/* Grup daveti: bilerek oturumsuz — davet edilenin hesabı henüz giriş yapamaz
          durumda (gölge hesap). Yetki, e-postayla gelen tek kullanımlık token. */}
      <Route path="/davet" element={<InvitationPage />} />
      <Route path="/change-password" element={
        <ProtectedRoute>
          <ChangePasswordPage />
        </ProtectedRoute>
      } />

      {/* 3D Secure Payment Result Routes */}
      <Route path="/payment/success" element={<PaymentSuccessPage />} />
      <Route path="/payment/error" element={<PaymentErrorPage />} />

      {/* Akbank Mobil ekosistem WebView giriş noktası — bilerek oturumsuz. Bu adres Akbank'ın
          Partner DB'sinde "WebBaseUrl" olarak kayıtlı olacak, yani SABİT kalmalı: sonraki
          fazda gelen kimlik köprüsü de aynı yolun arkasına takılır, Akbank tarafında iş çıkmaz.

          İki yol birden kayıtlı, çünkü Akbank'ın verdiği taslak örnek URL'i
          `dis-firma.com/id=<token>` diye yazıyor (yol segmenti), sorgu dizesi olarak değil —
          hangisinin kastedildiği henüz teyit edilmedi. Aşağıdaki catch-all'dan ÖNCE gelmesi
          zorunlu: aksi halde `<Navigate to="/">` sorgu dizesini atar ve token sessizce kaybolur. */}
      <Route path="/partner/akbank" element={<AkbankChannelPage />} />
      <Route path="/partner/akbank/*" element={<AkbankChannelPage />} />

      <Route path="/" element={
        state.currentUser
          ? <Navigate to={getLandingPath(state.currentUser, state.welcomeSeen)} replace />
          : <Navigate to="/login" replace />
      } />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
    </Suspense>

    {/* Floating chat widget: available on every authenticated page except the full /chat
        page itself (avoid stacking two chat UIs). Sibling of <Suspense>, not inside it,
        so it doesn't vanish during page-lazy-load transitions. */}
    {state.currentUser && !CHAT_WIDGET_HIDDEN_PATHS.includes(location.pathname) && <ChatWidget />}
    </>
  );
}

function App() {
  return (
    <ToastProvider>
      <AppProvider>
        <Router>
          <div className="App">
            <OnboardingTour />
            <AppRoutes />
          </div>
        </Router>
      </AppProvider>
    </ToastProvider>
  );
}

export default App;
